import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { css } from "goober";

import { readState } from "@/__main__/app-state.mjs";
import { IS_NODE } from "@/__main__/constants.mjs";
import { setRoute, updateRoute } from "@/__main__/router.mjs";
import {
  addFavoriteGame,
  removeFavoriteGame,
  swapFavoriteGames,
} from "@/app/actions.mjs";
import {
  ACTIVE_GAMES,
  GAME_BOX_ICONS,
  GAME_PREFERRED_NAME_MAP,
  GAME_SHORT_NAMES,
  HUB_NAME_MAP,
  HUB_SYMBOL_SETTINGS,
} from "@/app/constants.mjs";
import type { GameSymbol } from "@/app/games.mjs";
import gamesList from "@/app/games.mjs";
import IconAdd from "@/inline-assets/add.svg";
import IconAllGames from "@/inline-assets/all-games.svg";
import DontWantIcon from "@/inline-assets/dont-want.svg";
import {
  DropdownMenu,
  DropdownMenuItem,
  OverflowContainer,
  TabButton,
  TabsRow,
} from "@/shared/GameFilterBar.style.jsx";
import Ripple from "@/shared/Ripple.jsx";
import { useClientOnly } from "@/util/ClientOnly.jsx";
import debounce from "@/util/debounce.mjs";
import { useGameSymbol } from "@/util/game-route.mjs";
import partition from "@/util/partition.mjs";
import { useTransientRoute } from "@/util/router-hooks.mjs";
import useCallbackRef from "@/util/use-callback-ref.mjs";
import useDropdownRef from "@/util/use-dropdown-ref.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

export const GameIcon = ({ symbol }) => {
  const Icon = GAME_BOX_ICONS[symbol];
  if (!Icon) return null;
  return <Icon />;
};

// used to update the route path on selecting a game
function useGetGamePath() {
  const { hubSymbol } = useTransientRoute();
  return useCallback(
    (g?: GameSymbol) => {
      const routeParts = [HUB_NAME_MAP[hubSymbol], GAME_SHORT_NAMES[g]].filter(
        Boolean,
      );
      return "/" + routeParts.join("/");
    },
    [hubSymbol],
  );
}

type FavoriteGameOrder = Record<GameSymbol, number>;

type GameBarItemProps = {
  gameSymbol: GameSymbol;
  isSelected: boolean;
  onSelectGame?: (gameSymbol: GameSymbol) => void;
  onContextMenu?: (e: React.MouseEvent, gameSymbol: GameSymbol) => void;
};

const GameBarItem = ({
  gameSymbol,
  isSelected,
  onSelectGame,
  onContextMenu,
}: GameBarItemProps) => {
  const { t } = useTranslation();

  const getGamePath = useGetGamePath();

  const element = (
    <TabButton
      className={isSelected ? "selected" : ""}
      onClick={onSelectGame && (() => onSelectGame(gameSymbol))}
      onContextMenu={(e) => onContextMenu?.(e, gameSymbol)}
    >
      <GameIcon symbol={gameSymbol} />
      <label>{t(...GAME_PREFERRED_NAME_MAP[gameSymbol])}</label>
      <Ripple />
    </TabButton>
  );

  if (onSelectGame) {
    return element;
  }

  return (
    <a
      href={getGamePath(gameSymbol)}
      aria-label={t(...GAME_PREFERRED_NAME_MAP[gameSymbol])}
    >
      {element}
    </a>
  );
};

type GameBarProps = React.PropsWithChildren<
  Omit<React.ComponentPropsWithoutRef<"div">, "onContextMenu">
> & {
  games: ReadonlyArray<GameSymbol>;
  selected: GameSymbol | null;
  showAllGames: boolean;
  noAllGamesLink?: boolean;
  onSelectGame?: (game: GameSymbol) => void;

  onSwapItems?: (a: number, b: number) => void;
  onContextMenu?: (
    e: React.MouseEvent<HTMLDivElement>,
    game: GameSymbol,
  ) => void;
} & { gameOrder?: FavoriteGameOrder };

const GameBar = ({
  games = gamesList.map((g) => g.gameSymbol),
  selected,
  children,
  showAllGames,
  noAllGamesLink,
  onSelectGame,
  onContextMenu,
}: GameBarProps) => {
  const tabRowRef = useRef(null);

  // hides tab button labels when the container overflows
  // overflow is observed on resize and when children are added
  // to the subtree of the ref element
  const overflowRef = useCallbackRef((node: HTMLDivElement) => {
    // No-op this in server environment
    if (IS_NODE) return;

    function hideLabelsOnOverflow() {
      node.classList.remove("hide-labels");

      if (node.scrollWidth > node.clientWidth) {
        node.classList.add("hide-labels");
      }
    }

    const mutationObs = new MutationObserver(hideLabelsOnOverflow);

    const resizeObs =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(debounce(hideLabelsOnOverflow, 50));

    resizeObs?.observe(node);
    mutationObs.observe(node, { childList: true, subtree: true });

    return () => {
      resizeObs?.disconnect();
      mutationObs.disconnect();
    };
  }, []);

  const getGamePath = useGetGamePath();
  const { hubSymbol } = useTransientRoute();

  const { t } = useTranslation();

  const isSettingsHub = hubSymbol === HUB_SYMBOL_SETTINGS;
  const allGamesLabel = isSettingsHub
    ? t("common:general", "General")
    : t("common:allGames", "All Games");

  return (
    <OverflowContainer
      ref={overflowRef}
      onContextMenu={(e) => e.preventDefault()}
    >
      <TabsRow ref={tabRowRef}>
        {showAllGames && !noAllGamesLink ? (
          <a href={getGamePath()} aria-label={allGamesLabel}>
            <TabButton className={!selected ? "selected" : ""}>
              <IconAllGames />
              <label>{allGamesLabel}</label>
            </TabButton>
          </a>
        ) : showAllGames ? (
          <TabButton
            className={!selected && "selected"}
            onClick={onSelectGame && (() => onSelectGame(null))}
            onContextMenu={(e) => onContextMenu?.(e, null)}
          >
            <IconAllGames />
            <label>{allGamesLabel}</label>
            <Ripple />
          </TabButton>
        ) : null}

        {games.map((gameSymbol) => {
          return (
            <GameBarItem
              key={GAME_SHORT_NAMES[gameSymbol]}
              gameSymbol={gameSymbol}
              isSelected={gameSymbol === selected}
              onSelectGame={onSelectGame}
              onContextMenu={onContextMenu}
            />
          );
        })}

        {children}
      </TabsRow>
    </OverflowContainer>
  );
};

type GameFilterBarProps = React.ComponentPropsWithoutRef<"div"> & {
  games?: ReadonlyArray<GameSymbol>;
  extraGames?: ReadonlyArray<GameSymbol>;
  showAllGames?: boolean;
  noAllGamesLink?: boolean;

  selectedGame?: GameSymbol;
  onSelectGame?: (game: GameSymbol) => void;
};

export const GameFilterBar = ({
  games = gamesList.filter((g) => g.isActive === true).map((g) => g.gameSymbol),
  extraGames,
  showAllGames,
  noAllGamesLink,
  selectedGame,
  onSelectGame,
  ...rest
}: GameFilterBarProps) => {
  const gameSymbol = useGameSymbol();

  const selectedGameSymbol = selectedGame ?? gameSymbol;

  const [showGameDropdown, setShowGameDropdown] = useState(false);

  const containerRef = useRef(null);
  const gamesTabRef = useRef(null);

  // set position of dropdown relative to add game tab
  // close dropdown when clicking outside of the element or
  // when window is resized

  const positionGamesDropdown = useCallback((node) => {
    if (gamesTabRef.current && containerRef.current) {
      node.style.left = `${Math.min(
        gamesTabRef.current.offsetLeft,
        containerRef.current.clientWidth - node.clientWidth,
      )}px`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeGamesDropdown = useCallback(() => {
    setShowGameDropdown(null);
  }, []);

  const gamesDropdownRef = useDropdownRef({
    onClickOutside: closeGamesDropdown,
    onWindowResize: closeGamesDropdown,
    onAssignNode: positionGamesDropdown,
  });

  const onOpenGamesDropdown = useCallback((event) => {
    if (event.button === 0) {
      // stops our click-outside effect from firing on initial click
      // otherwise dropdown will go immediately from true back to false
      event.stopPropagation();

      setShowGameDropdown((val) => !val);
    }
  }, []);

  useEffect(() => {
    closeGamesDropdown();
  }, [selectedGameSymbol, closeGamesDropdown]);

  const getGamePath = useGetGamePath();

  const { t } = useTranslation();

  return (
    <div ref={containerRef} {...rest}>
      <GameBar
        games={games}
        selected={selectedGameSymbol}
        showAllGames={showAllGames ?? true}
        noAllGamesLink={noAllGamesLink ?? false}
        onSelectGame={onSelectGame}
      >
        {extraGames?.length ? (
          <TabButton
            ref={gamesTabRef}
            onMouseDown={onOpenGamesDropdown}
            tabIndex={0}
            role="button"
            aria-pressed={showGameDropdown ? "true" : "false"}
            style={{ order: 99 }}
            aria-label={t("common:more", "More")}
          >
            ...
          </TabButton>
        ) : null}
      </GameBar>

      {showGameDropdown ? (
        <DropdownMenu ref={gamesDropdownRef}>
          {extraGames.map((symbol) => (
            <a
              key={GAME_SHORT_NAMES[symbol]}
              href={getGamePath(symbol)}
              aria-label={t(...GAME_PREFERRED_NAME_MAP[symbol])}
            >
              <TabButton
                key={GAME_SHORT_NAMES[symbol]}
                $color="var(--shade0)"
                $background="transparent"
              >
                {<GameIcon symbol={symbol} />}
                {t(...GAME_PREFERRED_NAME_MAP[symbol])}
              </TabButton>
            </a>
          ))}
        </DropdownMenu>
      ) : null}
    </div>
  );
};

export const useFavoriteGames = () => {
  const {
    settings: { favoriteGames },
  } = useSnapshot(readState);

  return useMemo(
    () =>
      favoriteGames?.filter((g) =>
        (ACTIVE_GAMES as GameSymbol[]).includes(g),
      ) ?? ACTIVE_GAMES,
    [favoriteGames],
  );
};

export const useFavoriteGamesOrder = () => {
  const favoriteGames = useFavoriteGames();

  return useMemo<FavoriteGameOrder>(
    () =>
      favoriteGames.reduce(
        (agg, symbol, index) => Object.assign(agg, { [symbol]: index }),
        {} as FavoriteGameOrder,
      ),
    [favoriteGames],
  );
};

export const FavoriteGamesFilterBar = ({ ...rest }) => {
  const gameSymbol = useGameSymbol();

  const favoriteGames = useFavoriteGames();

  const [showGameDropdown, setShowGameDropdown] = useState(false);
  const [contextMenuGame, setContextMenuGame] = useState(null);
  const addGameTabRef = useRef(null);
  const containerRef = useRef(null);

  const isHydrated = useClientOnly();

  // partition into games in the tab row, and those that can be added via dropdown
  const [visibleGames, hiddenGames] = useMemo(
    () =>
      isHydrated
        ? partition(
            ACTIVE_GAMES,
            (symbol) => favoriteGames.includes(symbol) || symbol === gameSymbol,
          )
        : [[], ACTIVE_GAMES],
    [isHydrated, favoriteGames, gameSymbol],
  );

  // create a map of each game's rendered order in the tab row
  const gameOrder = useFavoriteGamesOrder();

  // set position of dropdown relative to add game tab
  // close dropdown when clicking outside of the element or
  // when window is resized

  const positionAddGameDropdown = useCallback((node: HTMLDivElement) => {
    if (addGameTabRef.current && containerRef.current) {
      node.style.left = `${Math.min(
        addGameTabRef.current.offsetLeft,
        containerRef.current.clientWidth - node.clientWidth,
      )}px`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeAddGameDropdown = useCallback(() => {
    setShowGameDropdown(null);
  }, []);

  const addGameDropdownRef = useDropdownRef({
    onClickOutside: closeAddGameDropdown,
    onWindowResize: closeAddGameDropdown,
    onAssignNode: positionAddGameDropdown,
  });

  const onOpenAddGameDropdown = useCallback((event) => {
    if (event.button === 0) {
      // stops our click-outside effect from firing on initial click
      // otherwise dropdown will go immediately from true back to false
      event.stopPropagation();

      setShowGameDropdown((val) => !val);
    }
  }, []);

  // set position of context menu relative to the game tab clicked
  // close menu when clicking outside of the element or
  // when window is resized
  const contextMenuNode = useRef<HTMLDivElement>();

  const positionContextMenu = useCallback(
    (node: HTMLDivElement) => {
      // using refs to determine which game tab was just clicked
      const { height } = contextMenuNode.current.getBoundingClientRect();

      node.style.top = `${height}px`;
      node.style.left = `${Math.min(
        contextMenuNode.current.offsetLeft + containerRef.current.offsetLeft,
        containerRef.current.clientWidth - node.clientWidth,
      )}px`;
    },
    [containerRef],
  );

  const closeContextMenu = useCallback(() => {
    setContextMenuGame(null);
    contextMenuNode.current = null;
  }, []);

  const contextMenuRef = useDropdownRef({
    onClickOutside: closeContextMenu,
    onWindowResize: closeContextMenu,
    onAssignNode: positionContextMenu,
  });

  const getGamePath = useGetGamePath();

  const onAddGame = useCallback(
    (gameSymbol) => {
      setShowGameDropdown(false);
      addFavoriteGame(gameSymbol);
      setRoute(getGamePath(gameSymbol), undefined, undefined, true);
    },
    [getGamePath],
  );

  const onRemoveGame = useCallback(
    (e) => {
      if (e.button === 0) {
        // clear from our row of favorite games
        removeFavoriteGame(contextMenuGame);
        // reset active tab if it was just removed
        if (gameSymbol === contextMenuGame) {
          updateRoute(getGamePath(), undefined, undefined, true);
        }
        setContextMenuGame(null);
      }
    },
    [gameSymbol, contextMenuGame, getGamePath],
  );

  const handleGameContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, gameSymbol: GameSymbol) => {
      setContextMenuGame(gameSymbol);
      contextMenuNode.current = event.currentTarget;
    },
    [],
  );

  useEffect(() => {
    setContextMenuGame(null);
  }, [gameSymbol]);

  const { t } = useTranslation();

  return (
    <div ref={containerRef} {...rest}>
      <GameBar
        games={visibleGames}
        selected={gameSymbol}
        showAllGames={true}
        gameOrder={gameOrder}
        onSwapItems={swapFavoriteGames}
        onContextMenu={handleGameContextMenu}
      >
        {hiddenGames?.length ? (
          <TabButton
            ref={addGameTabRef}
            onMouseDown={onOpenAddGameDropdown}
            tabIndex={0}
            role="button"
            aria-pressed={showGameDropdown ? "true" : "false"}
            style={{ order: 99 }}
            aria-label={t("common:addFavorite", "Add a Favorite")}
          >
            <IconAdd />
          </TabButton>
        ) : null}
      </GameBar>

      {showGameDropdown ? (
        <DropdownMenu ref={addGameDropdownRef}>
          <span>{t("common:addFavorite", "Add a Favorite")}</span>

          {hiddenGames.map((symbol) => (
            <TabButton
              key={GAME_SHORT_NAMES[symbol]}
              $color="var(--shade0)"
              $background="transparent"
              onMouseUp={() => onAddGame(symbol)}
              aria-label={t(...GAME_PREFERRED_NAME_MAP[symbol])}
            >
              {<GameIcon symbol={symbol} />}
              {t(...GAME_PREFERRED_NAME_MAP[symbol])}
            </TabButton>
          ))}
        </DropdownMenu>
      ) : null}

      {contextMenuGame ? (
        <DropdownMenu
          ref={contextMenuRef}
          className={css`
            margin-top: var(--sp-3);
          `}
        >
          <DropdownMenuItem
            className="type-form--button"
            onMouseUp={onRemoveGame}
            role="button"
            tabIndex={0}
          >
            <DontWantIcon />
            {t("common:removeFromFavorites", "Remove from Favorites")}
          </DropdownMenuItem>
        </DropdownMenu>
      ) : null}
    </div>
  );
};
